import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, MoreHorizontal, Loader2, Users, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { PageHeader, StatusBadge, PersonAvatar, EmptyState } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg, fmtDate, localToday } from "@/lib/api";
import { useEmployees } from "@/lib/useEmployees";
import { useAuth, isManager } from "@/context/AuthContext";

const EMPTY = { name: "", email: "", title: "", department: "", start_date: localToday(), status: "active", phone: "", location: "" };

function EmployeeDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const editing = !!initial?.employee_id;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { name: f.name, email: f.email, title: f.title, department: f.department, start_date: f.start_date, status: f.status, phone: f.phone, location: f.location };
    try {
      if (editing) await api.patch(`/employees/${initial.employee_id}`, body);
      else await api.post("/employees", body);
      toast.success(editing ? "Profile updated" : "Staff member added");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`employee-${k}-input`} {...props} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-lg max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit staff member" : "Add staff member"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {field("name", "Full name", { required: true })}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field("title", "Job title")}
            {field("department", "Department")}
            {field("email", "Email", { type: "email" })}
            {field("phone", "Phone")}
            <div className="space-y-1.5"><Label>Start date</Label><DatePicker value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} testid="employee-start-date-picker" /></div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="employee-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["active", "onboarding", "on_leave", "inactive"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {field("location", "Location")}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="employee-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}{editing ? "Save" : "Add"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function People() {
  const { user } = useAuth();
  const manager = isManager(user);
  const navigate = useNavigate();
  const { employees, reload } = useEmployees();
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const depts = useMemo(() => [...new Set(employees.map((e) => e.department).filter(Boolean))].sort(), [employees]);
  const list = employees.filter((e) =>
    (dept === "all" || e.department === dept) &&
    [e.name, e.title, e.email, e.department].join(" ").toLowerCase().includes(q.toLowerCase()));

  const remove = async (e) => {
    if (!window.confirm(`Remove ${e.name} from the directory?`)) return;
    try { await api.delete(`/employees/${e.employee_id}`); toast.success("Removed"); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const onboard = async (e) => {
    try { await api.post("/onboarding", { employee_id: e.employee_id }); toast.success(`Onboarding started for ${e.name}`); reload(); navigate("/onboarding"); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const actions = (e) => manager && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label="Actions" data-testid="people-row-actions"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setDialog({ open: true, initial: e })} data-testid="people-edit-action">Edit profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onboard(e)} data-testid="people-onboard-action">Start onboarding</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/ai-ceo?q=${encodeURIComponent(`Give me a quick overview of ${e.name}'s workload, meetings and leave`)}`)}>Ask CEO about workload</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive" onClick={() => remove(e)} data-testid="people-delete-action">Remove</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div data-testid="people-page">
      <PageHeader title="Staff directory" subtitle={`${employees.length} people`}
        actions={manager && <Button onClick={() => setDialog({ open: true, initial: null })} data-testid="people-add-button"><Plus className="h-4 w-4 mr-1.5" />Add staff</Button>} />
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, title, email" className="pl-9 bg-card" data-testid="people-directory-search-input" />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="sm:w-52 bg-card" data-testid="people-department-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All departments</SelectItem>{depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {list.length === 0 ? (
        <div className="bg-card border border-border rounded-xl"><EmptyState icon={Users} title="No staff found" text={employees.length ? "Try a different search." : "Add your first team member."} /></div>
      ) : (
        <>
          <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden">
            <Table data-testid="people-directory-table">
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Title</TableHead><TableHead>Department</TableHead><TableHead>Start date</TableHead><TableHead>Status</TableHead><TableHead className="w-12" /></TableRow></TableHeader>
              <TableBody>
                {list.map((e) => (
                  <TableRow key={e.employee_id} data-testid="people-row">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <PersonAvatar name={e.name} />
                        <div><p className="font-medium">{e.name}{e.user_id && <span className="ml-2 text-[10px] uppercase tracking-wider text-primary">user</span>}</p><p className="text-xs text-muted-foreground">{e.email}</p></div>
                      </div>
                    </TableCell>
                    <TableCell>{e.title}</TableCell>
                    <TableCell>{e.department}</TableCell>
                    <TableCell className="tabular-nums">{fmtDate(e.start_date, { month: "short", day: "numeric", year: "numeric" })}</TableCell>
                    <TableCell><StatusBadge status={e.status} /></TableCell>
                    <TableCell>{actions(e)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="md:hidden grid gap-3">
            {list.map((e) => (
              <div key={e.employee_id} className="bg-card border border-border rounded-xl p-4 flex items-start gap-3" data-testid="people-card">
                <PersonAvatar name={e.name} className="h-10 w-10" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{e.name}</p>
                  <p className="text-xs text-muted-foreground">{e.title} · {e.department}</p>
                  {e.email && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1 truncate"><Mail className="h-3 w-3" />{e.email}</p>}
                  <div className="mt-2"><StatusBadge status={e.status} /></div>
                </div>
                {actions(e)}
              </div>
            ))}
          </div>
        </>
      )}
      <EmployeeDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} initial={dialog.initial} onSaved={reload} />
    </div>
  );
}
